/**
 * Signup Page
 * New user registration with email verification (OTP)
 * Always creates a new tenant with admin role
 * Styled to match G6th Sense branding
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, UserPlus, Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import api from '../utils/api';

// ============================================================================
// Extracted Render Components
// ============================================================================

const OTPVerificationStep = ({
    formData,
    otp,
    otpError,
    isSubmitting,
    isSendingCode,
    resendTimer,
    otpRefs,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    handleVerifyAndSignup,
    handleResendCode,
    setStep
}) => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-orange-50 to-rose-100 p-4">
        <Card className="w-full max-w-md shadow-2xl">
            <CardHeader className="space-y-1 text-center">
                <div className="flex justify-center mb-4">
                    <div className="h-16 w-16 rounded-full bg-[var(--g6-primary)]/10 flex items-center justify-center">
                        <Mail className="h-8 w-8 text-[var(--g6-primary)]" />
                    </div>
                </div>
                <CardTitle className="text-2xl font-bold text-gray-800">
                    Verify Your Email
                </CardTitle>
                <CardDescription className="text-gray-600">
                    We've sent a 6-digit code to<br />
                    <span className="font-medium text-gray-800">{formData.email}</span>
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
                {otpError && (
                    <Alert variant="destructive">
                        <AlertDescription>{otpError}</AlertDescription>
                    </Alert>
                )}

                {/* OTP Input */}
                <div className="flex justify-center gap-2">
                    {otp.map((digit, index) => (
                        <input
                            key={index}
                            ref={el => otpRefs.current[index] = el}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            onPaste={handleOtpPaste}
                            className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-lg focus:border-[var(--g6-primary)] focus:ring-2 focus:ring-[var(--g6-primary)]/20 outline-none transition-all"
                            disabled={isSubmitting}
                        />
                    ))}
                </div>

                {/* Verify Button */}
                <Button
                    onClick={handleVerifyAndSignup}
                    className="w-full g6-btn g6-btn-primary text-white font-semibold py-6 text-base"
                    disabled={isSubmitting || otp.join('').length !== 6}
                >
                    {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Verifying...
                        </span>
                    ) : (
                        <span className="flex items-center justify-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            Verify & Create Account
                        </span>
                    )}
                </Button>

                {/* Resend Code */}
                <div className="text-center text-sm text-gray-600">
                    Didn't receive the code?{' '}
                    {resendTimer > 0 ? (
                        <span className="text-gray-500">Resend in {resendTimer}s</span>
                    ) : (
                        <button
                            onClick={handleResendCode}
                            disabled={isSendingCode}
                            className="text-[var(--g6-primary)] hover:underline font-medium disabled:opacity-50"
                        >
                            {isSendingCode ? 'Sending...' : 'Resend Code'}
                        </button>
                    )}
                </div>

                {/* Back Button */}
                <button
                    onClick={() => setStep('form')}
                    className="flex items-center justify-center gap-1 text-sm text-gray-600 hover:text-gray-800 mx-auto"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to signup form
                </button>
            </CardContent>
        </Card>
    </div>
);

const SuccessStep = () => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-orange-50 to-rose-100 p-4">
        <Card className="w-full max-w-md shadow-2xl">
            <CardContent className="py-12 text-center">
                <div className="flex justify-center mb-6">
                    <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                        <CheckCircle className="h-10 w-10 text-green-600" />
                    </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    Account Created!
                </h2>
                <p className="text-gray-600 mb-4">
                    Your account has been created successfully.
                </p>
                <p className="text-sm text-gray-500">
                    Redirecting to login...
                </p>
            </CardContent>
        </Card>
    </div>
);

const SignupFormStep = ({
    formData,
    error,
    requireOtp,
    showPassword,
    showConfirmPassword,
    isSendingCode,
    isSubmitting,
    isLoading,
    configLoading,
    handleChange,
    handleFormSubmit,
    setShowPassword,
    setShowConfirmPassword
}) => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 via-orange-50 to-rose-100 p-4">
        <Card className="w-full max-w-md shadow-2xl">
            {/* Logo Section */}
            <div className="flex justify-center pt-8 pb-4">
                <div className="bg-white rounded-2xl p-4 shadow-lg">
                    <div className="g6-logo">
                        <div className="g6-logo-icon">
                            G6
                        </div>
                        <div className="g6-logo-text">
                            <span className="g6-text">6</span>
                            <sup className="text-sm">th</sup>
                            <span className="sense-text"> Sense</span>
                        </div>
                    </div>
                </div>
            </div>

            <CardHeader className="space-y-1 text-center pt-2">
                <CardTitle className="text-2xl font-bold text-gray-800">
                    Create Account
                </CardTitle>
                <CardDescription className="text-gray-600">
                    Sign up to get started with G6th Sense
                </CardDescription>
            </CardHeader>

            <CardContent>
                <form onSubmit={handleFormSubmit} className="space-y-4">
                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="space-y-2">
                        <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
                        <Input
                            id="name"
                            name="name"
                            type="text"
                            placeholder="John Doe"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={isSendingCode || isSubmitting || isLoading}
                            required
                            className="border-gray-300"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="john@company.com"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={isSendingCode || isSubmitting || isLoading}
                            required
                            className="border-gray-300"
                        />
                        {requireOtp && (
                            <p className="text-xs text-gray-500">
                                A verification code will be sent to this email
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-gray-700 font-medium">Password *</Label>
                        <div className="relative">
                            <Input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="Min. 8 characters"
                                value={formData.password}
                                onChange={handleChange}
                                disabled={isSendingCode || isSubmitting || isLoading}
                                required
                                className="border-gray-300 pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="confirmPassword" className="text-gray-700 font-medium">Confirm Password *</Label>
                        <div className="relative">
                            <Input
                                id="confirmPassword"
                                name="confirmPassword"
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Confirm your password"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                disabled={isSendingCode || isSubmitting || isLoading}
                                required
                                className="border-gray-300 pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            >
                                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full g6-btn g6-btn-primary text-white font-semibold py-6 text-base"
                        disabled={isSendingCode || isSubmitting || isLoading || configLoading}
                    >
                        {(isSendingCode || isSubmitting) ? (
                            <span className="flex items-center justify-center gap-2">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                {requireOtp ? 'Sending Code...' : 'Creating Account...'}
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2">
                                {requireOtp ? <Mail className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                                {requireOtp ? 'Send Verification Code' : 'Create Account'}
                            </span>
                        )}
                    </Button>
                </form>
            </CardContent>

            <CardFooter className="flex flex-col space-y-4 pb-6">
                <div className="text-sm text-center text-gray-600">
                    Already have an account?{' '}
                    <Link
                        to="/login"
                        className="text-[var(--g6-primary)] hover:text-[var(--g6-primary-dark)] hover:underline font-semibold"
                    >
                        Sign in
                    </Link>
                </div>
            </CardFooter>
        </Card>
    </div>
);

// ============================================================================
// Main Signup Component
// ============================================================================

const Signup = () => {
    const navigate = useNavigate();
    const { isLoading } = useAuth();

    // Form state
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
    });

    // OTP state
    const [step, setStep] = useState('form'); // 'form' | 'otp' | 'success'
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [otpError, setOtpError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);
    const otpRefs = useRef([]);

    // UI state
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // OTP requirement state
    const [requireOtp, setRequireOtp] = useState(true);
    const [configLoading, setConfigLoading] = useState(true);

    // Helper: Validate form inputs
    const validateForm = () => {
        if (!formData.email || !formData.name || !formData.password || !formData.confirmPassword) {
            return 'Please fill in all required fields';
        }

        if (!validateEmail(formData.email)) {
            return 'Please enter a valid email address';
        }

        if (formData.password.length < 8) {
            return 'Password must be at least 8 characters long';
        }

        if (formData.password !== formData.confirmPassword) {
            return 'Passwords do not match';
        }

        return null;
    };

    // Helper: Request OTP verification code
    const requestOtpCode = async () => {
        setIsSendingCode(true);
        try {
            const response = await api.post('/api/auth/signup/request-code', {
                email: formData.email
            });

            if (response.data.success) {
                setStep('otp');
                setResendTimer(60);
                setOtp(['', '', '', '', '', '']);
            } else {
                setError(response.data.message || 'Failed to send verification code');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.detail?.message
                || err.response?.data?.detail
                || 'Failed to send verification code. Please try again.';
            setError(errorMessage);
        } finally {
            setIsSendingCode(false);
        }
    };

    // Helper: Handle direct signup (no OTP)
    const handleDirectSignup = async () => {
        setIsSubmitting(true);
        try {
            const response = await api.post('/api/auth/signup', {
                email: formData.email,
                password: formData.password,
                name: formData.name
            });

            if (response.data.tenant_id) {
                setStep('success');
                setTimeout(() => {
                    navigate('/login', {
                        state: {
                            message: 'Account created successfully! Please log in.',
                            email: formData.email
                        }
                    });
                }, 2000);
            } else {
                setError('Signup failed. Please try again.');
            }
        } catch (err) {
            const errorDetail = err.response?.data?.detail;
            const errorMessage = typeof errorDetail === 'object'
                ? errorDetail.message
                : errorDetail || 'Signup failed. Please try again.';
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Helper: Extract error message from response
    const extractErrorMessage = (err, defaultMessage = 'Verification failed. Please try again.') => {
        const errorDetail = err.response?.data?.detail;
        return typeof errorDetail === 'object'
            ? errorDetail.message
            : errorDetail || defaultMessage;
    };

    // Helper: Navigate to login with success message
    const navigateToLogin = () => {
        setTimeout(() => {
            navigate('/login', {
                state: {
                    message: 'Account created successfully! Please log in.',
                    email: formData.email
                }
            });
        }, 2000);
    };

    // Fetch signup config on mount
    useEffect(() => {
        const fetchSignupConfig = async () => {
            try {
                const response = await api.get('/api/auth/signup/config');
                setRequireOtp(response.data.require_otp !== false);
            } catch (err) {
                // Default to requiring OTP if config fetch fails
                setRequireOtp(true);
            } finally {
                setConfigLoading(false);
            }
        };
        fetchSignupConfig();
    }, []);

    // Resend timer countdown
    useEffect(() => {
        if (resendTimer > 0) {
            const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendTimer]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (error) setError('');
    };

    const validateEmail = (email) => {
        // Bounded quantifiers to prevent ReDoS - max 64 chars local, 255 domain
        const emailRegex = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{1,63}$/;
        return emailRegex.test(email);
    };

    // Handle OTP input
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return; // Only allow digits

        const newOtp = [...otp];
        newOtp[index] = value.slice(-1); // Only take last character
        setOtp(newOtp);
        setOtpError('');

        // Auto-focus next input
        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 6);
        if (/^\d+$/.test(pastedData)) {
            const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
            setOtp(newOtp);
            // Focus the last filled input or the next empty one
            const focusIndex = Math.min(pastedData.length, 5);
            otpRefs.current[focusIndex]?.focus();
        }
    };

    // Handle form submit - either request OTP or direct signup
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (requireOtp) {
            await requestOtpCode();
        } else {
            await handleDirectSignup();
        }
    };

    // Step 2: Complete signup with OTP
    const handleVerifyAndSignup = async () => {
        const verificationCode = otp.join('');

        if (verificationCode.length !== 6) {
            setOtpError('Please enter the complete 6-digit code');
            return;
        }

        setIsSubmitting(true);
        setOtpError('');

        try {
            const response = await api.post('/api/auth/signup', {
                email: formData.email,
                password: formData.password,
                name: formData.name,
                verification_code: verificationCode
            });

            if (response.data.tenant_id) {
                setStep('success');
                navigateToLogin();
            } else {
                setOtpError('Signup failed. Please try again.');
            }
        } catch (err) {
            const errorMessage = extractErrorMessage(err);
            setOtpError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Resend verification code
    const handleResendCode = async () => {
        if (resendTimer > 0) return;

        setIsSendingCode(true);
        setOtpError('');

        try {
            const response = await api.post('/api/auth/signup/request-code', {
                email: formData.email
            });

            if (response.data.success) {
                setResendTimer(60);
                setOtp(['', '', '', '', '', '']);
            }
        } catch (err) {
            setOtpError('Failed to resend code. Please try again.');
        } finally {
            setIsSendingCode(false);
        }
    };

    // Render based on step
    if (step === 'otp') {
        return (
            <OTPVerificationStep
                formData={formData}
                otp={otp}
                otpError={otpError}
                isSubmitting={isSubmitting}
                isSendingCode={isSendingCode}
                resendTimer={resendTimer}
                otpRefs={otpRefs}
                handleOtpChange={handleOtpChange}
                handleOtpKeyDown={handleOtpKeyDown}
                handleOtpPaste={handleOtpPaste}
                handleVerifyAndSignup={handleVerifyAndSignup}
                handleResendCode={handleResendCode}
                setStep={setStep}
            />
        );
    }

    if (step === 'success') {
        return <SuccessStep />;
    }

    return (
        <SignupFormStep
            formData={formData}
            error={error}
            requireOtp={requireOtp}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            isSendingCode={isSendingCode}
            isSubmitting={isSubmitting}
            isLoading={isLoading}
            configLoading={configLoading}
            handleChange={handleChange}
            handleFormSubmit={handleFormSubmit}
            setShowPassword={setShowPassword}
            setShowConfirmPassword={setShowConfirmPassword}
        />
    );
};

export default Signup;